import { useState, useCallback, useRef, useEffect } from "react";
import { adminApiService } from "../utils/api";
import { useAuthHeaders } from "./useAuthHeaders";
import { ImageKeywordSuggestionItemDto } from "@swagger/typescript-fetch-goflyer";

interface UseImageKeywordSuggestionsOptions {
  /**
   * Delay in milliseconds before making the API call after user stops typing
   * @default 300
   */
  debounceDelay?: number;
  /**
   * Maximum number of suggestions to return
   * @default 10
   */
  limit?: number;
  /**
   * Minimum length of query before making API calls
   * @default 2
   */
  minQueryLength?: number;
}

interface UseImageKeywordSuggestionsReturn {
  /**
   * Array of keyword suggestions
   */
  suggestions: ImageKeywordSuggestionItemDto[];
  /**
   * Loading state - true when API call is in progress
   */
  isLoading: boolean;
  /**
   * Error state - contains error message if request fails
   */
  error: string | null;
  /**
   * Function to fetch suggestions for a given query
   */
  fetchSuggestions: (query: string) => void;
  /**
   * Function to clear current suggestions
   */
  clearSuggestions: () => void;
  /**
   * Current query being processed
   */
  currentQuery: string;
}

/**
 * Custom hook for fetching image keyword suggestions
 *
 * @param options Configuration options for the hook
 * @returns Object containing suggestions, loading state, error state, and utility functions
 *
 * @example
 * ```tsx
 * const {
 *   suggestions,
 *   isLoading,
 *   error,
 *   fetchSuggestions,
 *   clearSuggestions
 * } = useImageKeywordSuggestions({
 *   debounceDelay: 300,
 *   limit: 10,
 *   minQueryLength: 2
 * });
 *
 * // In your component
 * useEffect(() => {
 *   if (inputValue.length >= 2) {
 *     fetchSuggestions(inputValue);
 *   } else {
 *     clearSuggestions();
 *   }
 * }, [inputValue]);
 * ```
 */
export const useImageKeywordSuggestions = (
  options: UseImageKeywordSuggestionsOptions = {}
): UseImageKeywordSuggestionsReturn => {
  const { debounceDelay = 300, limit = 10, minQueryLength = 2 } = options;

  const [suggestions, setSuggestions] = useState<
    ImageKeywordSuggestionItemDto[]
  >([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentQuery, setCurrentQuery] = useState("");

  const authHeaders = useAuthHeaders();
  const abortControllerRef = useRef<AbortController | null>(null);
  const debounceTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  /**
   * Function to make the actual API call
   */
  const fetchSuggestionsFromAPI = useCallback(
    async (query: string) => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }

      if (query.length < minQueryLength) {
        setSuggestions([]);
        setCurrentQuery("");
        setIsLoading(false);
        return;
      }

      abortControllerRef.current = new AbortController();

      setIsLoading(true);
      setError(null);
      setCurrentQuery(query);

      try {
        const response =
          await adminApiService.gfImageAdminControllerGetImageKeywordSuggestions(
            query,
            limit,
            {
              ...authHeaders,
              signal: abortControllerRef.current.signal,
            }
          );

        // Only update state if this request wasn't aborted
        if (!abortControllerRef.current.signal.aborted) {
          setSuggestions(response.suggestions || []);
          setIsLoading(false);
        }
      } catch (err: any) {
        // Only set error if request wasn't aborted
        if (!abortControllerRef.current.signal.aborted) {
          console.error("Error fetching image keyword suggestions:", err);
          setSuggestions([]);

          if (process.env.REACT_APP_IS_DEV !== `false`) {
            setError(err.message || "Failed to fetch suggestions");
          }
          setIsLoading(false);
        }
      }
    },
    [authHeaders, limit, minQueryLength]
  );

  /**
   * Public function to fetch suggestions with manual debouncing
   */
  const fetchSuggestions = useCallback(
    (query: string) => {
      const trimmedQuery = query.trim();

      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }

      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }

      if (trimmedQuery.length === 0) {
        setSuggestions([]);
        setCurrentQuery("");
        setError(null);
        setIsLoading(false);
        return;
      }

      if (trimmedQuery.length < minQueryLength) {
        setSuggestions([]);
        setCurrentQuery("");
        setError(null);
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      setError(null);

      debounceTimeoutRef.current = setTimeout(() => {
        fetchSuggestionsFromAPI(trimmedQuery);
      }, debounceDelay);
    },
    [fetchSuggestionsFromAPI, debounceDelay, minQueryLength]
  );

  /**
   * Function to clear suggestions
   */
  const clearSuggestions = useCallback(() => {
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }

    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    setSuggestions([]);
    setIsLoading(false);
    setError(null);
    setCurrentQuery("");
  }, []);

  useEffect(() => {
    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  return {
    suggestions,
    isLoading,
    error,
    fetchSuggestions,
    clearSuggestions,
    currentQuery,
  };
};
